import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Star, Check, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type DeliveryPartner } from "@/types/admin.ui";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import {
  useAdminDeliveryPartners,
  useApproveDeliveryPartnerMutation,
  useSuspendDeliveryPartnerMutation,
} from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const statusBadge: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  inactive: "bg-muted text-muted-foreground border-border",
  pending: "bg-warning/10 text-warning border-warning/20",
};

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All Status", value: "all" },
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
      { label: "Pending", value: "pending" },
    ],
  },
];

const AdminDelivery = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminDeliveryPartners(1, 100);
  const approveMutation = useApproveDeliveryPartnerMutation();
  const suspendMutation = useSuspendDeliveryPartnerMutation();
  const [pendingId, setPendingId] = useState<number | null>(null);

  const partners = data?.items ?? [];

  const table = useDataTable<DeliveryPartner>({
    data: partners,
    searchKeys: ["name", "phone"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) =>
      state.filters.status === "all" || row.status === state.filters.status,
  });

  const handleApprove = async (id: string) => {
    setPendingId(Number(id));
    try {
      await approveMutation.mutateAsync({ id: Number(id), approve: true });
      toast({ title: "Delivery partner approved" });
    } catch (error) {
      toast({ title: "Failed to approve partner", description: getApiErrorMessage(error), variant: "destructive" });
    } finally {
      setPendingId(null);
    }
  };

  const handleDeactivate = async (id: string) => {
    setPendingId(Number(id));
    try {
      await suspendMutation.mutateAsync({ id: Number(id), reason: "Suspended by admin" });
      toast({ title: "Delivery partner deactivated", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to deactivate partner", description: getApiErrorMessage(error), variant: "destructive" });
    } finally {
      setPendingId(null);
    }
  };

  const stats = useMemo(
    () => ({
      active: partners.filter((p) => p.status === "active").length,
      totalDeliveries: partners.reduce((sum, p) => sum + p.totalDeliveries, 0),
      totalEarnings: partners.reduce((sum, p) => sum + p.totalEarnings, 0),
    }),
    [partners],
  );

  const columns: DataTableColumn<DeliveryPartner>[] = [
    {
      id: "name",
      header: "Partner",
      accessorKey: "name",
      sortable: true,
      mobilePriority: 1,
      cell: (partner) => (
        <Link to={`/admin/delivery/${partner.id}`} className="block hover:opacity-80">
          <p className="font-semibold text-primary">{partner.name}</p>
          <p className="text-xs text-muted-foreground">{partner.phone}</p>
        </Link>
      ),
    },
    {
      id: "vehicleType",
      header: "Vehicle",
      accessorKey: "vehicleType",
      sortable: true,
      mobileLabel: "Vehicle",
      mobilePriority: 2,
      className: "text-sm",
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 3,
      cell: (partner) => (
        <Badge variant="outline" className={statusBadge[partner.status]}>
          {partner.status}
        </Badge>
      ),
    },
    {
      id: "totalDeliveries",
      header: "Deliveries",
      accessorKey: "totalDeliveries",
      sortable: true,
      align: "right",
      mobileLabel: "Deliveries",
      mobilePriority: 4,
    },
    {
      id: "rating",
      header: "Rating",
      accessorKey: "rating",
      sortable: true,
      align: "center",
      hideOnMobile: true,
      cell: (partner) => (partner.rating > 0 ? `⭐ ${partner.rating}` : "—"),
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (partner) => (
        <div className="flex items-center justify-center gap-1">
          <Button size="sm" variant="outline" asChild>
            <Link to={`/admin/delivery/${partner.id}`}>
              <Eye className="mr-1 h-3.5 w-3.5" /> Review
            </Link>
          </Button>
          {partner.status === "pending" && (
            <Button size="sm" onClick={() => handleApprove(partner.id)} disabled={pendingId === Number(partner.id)}>
              Approve
            </Button>
          )}
          {partner.status === "active" && (
            <Button size="sm" variant="outline" onClick={() => handleDeactivate(partner.id)} disabled={pendingId === Number(partner.id)}>
              Deactivate
            </Button>
          )}
          {partner.status === "inactive" && (
            <Button size="sm" onClick={() => handleApprove(partner.id)} disabled={pendingId === Number(partner.id)}>
              Reactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Partners"
        description="Manage delivery partner onboarding and performance."
      />

      <StatsGrid columns={3}>
        <CompactStatCard title="Active Partners" value={stats.active} icon={Truck} />
        <CompactStatCard title="Total Deliveries" value={stats.totalDeliveries} icon={Check} />
        <CompactStatCard title="Total Earnings" value={`₹${(stats.totalEarnings / 1000).toFixed(0)}K`} icon={Star} />
      </StatsGrid>

      <DataTable
        title="All Delivery Partners"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search partners..."
        filters={statusFilters}
        filterValues={table.state.filters}
        onFilterChange={table.setFilter}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        renderMobileCard={(partner) => (
          <div className="space-y-3 rounded-lg border border-border p-4">
            <div className="flex items-start justify-between">
              <Link to={`/admin/delivery/${partner.id}`} className="min-w-0">
                <p className="font-semibold text-primary">{partner.name}</p>
                <p className="text-xs text-muted-foreground">
                  {partner.phone} • {partner.vehicleType}
                </p>
              </Link>
              <Badge variant="outline" className={statusBadge[partner.status]}>
                {partner.status}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" asChild>
                <Link to={`/admin/delivery/${partner.id}`}>
                  <Eye className="mr-1 h-3 w-3" /> Review
                </Link>
              </Button>
              {partner.status === "pending" && (
                <Button size="sm" className="flex-1" onClick={() => handleApprove(partner.id)} disabled={pendingId === Number(partner.id)}>
                  <Check className="mr-1 h-3 w-3" /> Approve
                </Button>
              )}
              {partner.status === "active" && (
                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleDeactivate(partner.id)} disabled={pendingId === Number(partner.id)}>
                  Deactivate
                </Button>
              )}
              {partner.status === "inactive" && (
                <Button size="sm" className="flex-1" onClick={() => handleApprove(partner.id)} disabled={pendingId === Number(partner.id)}>
                  Reactivate
                </Button>
              )}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default AdminDelivery;
